import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  ArrowLeftRight,
  Clock,
  Calendar,
  User,
  AlertCircle,
  Plus,
  Trash2,
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-600'
};

const DUTY_LABELS = {
  front_desk: 'Front Desk',
  coaching: 'Coaching',
  academy_management: 'Academy Management'
};

const ShiftSwaps = () => {
  const [loading, setLoading] = useState(true);
  const [swapRequests, setSwapRequests] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    shift_id: '',
    target_staff_id: '',
    reason: ''
  });
  
  // Cancel confirmation
  const [cancelSwapDialog, setCancelSwapDialog] = useState(false);
  const [swapToCancel, setSwapToCancel] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [swapsRes, shiftsRes, staffRes] = await Promise.all([
        api.get('/staff/shift-swaps'),
        api.get('/staff/shifts'),
        api.get('/staff')
      ]);
      setSwapRequests(swapsRes.data);
      setMyShifts(shiftsRes.data);
      setStaffList(staffRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      shift_id: '',
      target_staff_id: '',
      reason: ''
    });
  };

  const handleSubmit = async () => {
    if (!formData.shift_id || !formData.target_staff_id) {
      toast.error('Please select a shift and target staff member');
      return;
    }

    try {
      await api.post('/staff/shift-swaps', formData);
      toast.success('Swap request submitted');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit swap request');
    }
  };

  const openCancelSwapDialog = (swapId) => {
    setSwapToCancel(swapId);
    setCancelSwapDialog(true);
  };

  const handleCancel = async () => {
    if (!swapToCancel) return;
    
    try {
      await api.delete(`/staff/shift-swaps/${swapToCancel}`);
      toast.success('Swap request cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel swap request');
    } finally {
      setCancelSwapDialog(false);
      setSwapToCancel(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Get available shifts for swap (exclude shifts with pending requests)
  const pendingShiftIds = swapRequests
    .filter(s => s.status === 'pending')
    .map(s => s.shift_id);
  
  const availableShifts = myShifts.filter(s => !pendingShiftIds.includes(s.id));

  // Get other staff members (not me)
  const otherStaff = staffList.filter(s => 
    s.id !== swapRequests[0]?.requester_id && 
    s.role === 'staff'
  );

  if (loading) {
    return (
      <DashboardLayout title="Shift Swaps">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Shift Swaps">
      <div className="space-y-6" data-testid="shift-swaps-page">
        {/* Header */}
        <div className="space-y-3">
          <div>
            <p className="text-slate-600">Request to swap shifts with other staff members</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-blue-500 hover:bg-blue-600"
            data-testid="new-swap-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Request Swap
          </Button>
          </div>
        </div>

        {/* Swap Requests List */}
        {swapRequests.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="p-8 lg:p-12 text-center">
              <ArrowLeftRight className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                No Swap Requests
              </h3>
              <p className="text-slate-500">
                You haven't made any shift swap requests yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {swapRequests.map((swap) => (
              <Card key={swap.id} className="border border-slate-200" data-testid={`swap-${swap.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge className={STATUS_COLORS[swap.status]}>
                          {swap.status.charAt(0).toUpperCase() + swap.status.slice(1)}
                        </Badge>
                        {swap.duty_type && (
                          <span className="text-sm text-slate-500">
                            {DUTY_LABELS[swap.duty_type]}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Shift Details */}
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 mb-1">Shift</p>
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            <span className="font-medium">{formatDate(swap.shift_date)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-slate-400" />
                            <span className="text-sm">
                              {formatTime(swap.shift_start_time)} - {formatTime(swap.shift_end_time)}
                            </span>
                          </div>
                        </div>

                        {/* Swap Partner */}
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-blue-600 mb-1">Swap With</p>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-blue-400" />
                            <span className="font-medium text-blue-700">{swap.target_staff_name}</span>
                          </div>
                        </div>
                      </div>

                      {swap.reason && (
                        <p className="text-sm text-slate-600 mt-3 flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          {swap.reason}
                        </p>
                      )}

                      {swap.admin_notes && swap.status !== 'pending' && (
                        <p className="text-sm text-slate-500 mt-2 italic">
                          Admin: {swap.admin_notes}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    {swap.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openCancelSwapDialog(swap.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`cancel-swap-${swap.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Cancel Swap Confirmation Dialog */}
        <AlertDialog open={cancelSwapDialog} onOpenChange={setCancelSwapDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                Cancel Swap Request
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel this swap request? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Request</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancel}
                className="bg-red-500 hover:bg-red-600"
              >
                Cancel Request
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* New Swap Request Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <ArrowLeftRight className="h-5 w-5 text-blue-500" />
                Request Shift Swap
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {availableShifts.length === 0 ? (
                <div className="text-center py-6">
                  <AlertCircle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
                  <p className="text-slate-600">
                    You don't have any upcoming shifts available for swap.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Select Shift to Swap</Label>
                    <Select
                      value={formData.shift_id}
                      onValueChange={(value) => setFormData({ ...formData, shift_id: value })}
                    >
                      <SelectTrigger data-testid="swap-shift-select">
                        <SelectValue placeholder="Choose a shift" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableShifts.map((shift) => (
                          <SelectItem key={shift.id} value={shift.id}>
                            {formatDate(shift.date)} - {formatTime(shift.start_time)} ({DUTY_LABELS[shift.duty_type]})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Swap With</Label>
                    <Select
                      value={formData.target_staff_id}
                      onValueChange={(value) => setFormData({ ...formData, target_staff_id: value })}
                    >
                      <SelectTrigger data-testid="swap-target-select">
                        <SelectValue placeholder="Select staff member" />
                      </SelectTrigger>
                      <SelectContent>
                        {otherStaff.map((staff) => (
                          <SelectItem key={staff.id} value={staff.id}>
                            {staff.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Reason (Optional)</Label>
                    <Textarea
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      placeholder="Why do you need to swap this shift?"
                      rows={3}
                      data-testid="swap-reason-input"
                    />
                  </div>
                </>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              {availableShifts.length > 0 && (
                <Button 
                  onClick={handleSubmit}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="submit-swap-btn"
                >
                  Submit Request
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ShiftSwaps;
